// Logique pure des écrans des contrôles : tons des états, libellés, marqueurs de la carte, grille de
// saisie d'un constat (réponses par point, contrôle local avant l'envoi). Testée seule.
import {
  CONCLUSIONS_CONSTAT,
  ETATS_CONSTAT,
  ETATS_MESURE,
  ETATS_ORDRE_MISSION,
  OBJETS_ORDRE_MISSION,
  verifierGrilleConstat,
  type ConclusionConstat,
  type EtatConstat,
  type EtatMesure,
  type EtatOrdreMission,
  type ObjetOrdreMission,
  type PointFormulaire,
  type ReponseConstat,
  type ReponsePoint,
} from '@cc/contrats';
import type { MarqueurCarte, TonEtat } from '@cc/ui';
import type { CarteControles } from './types';

export const TON_ORDRE: Record<EtatOrdreMission, TonEtat> = { emis: 'attente', en_cours: 'attente', constat_recu: 'ok', conflit: 'retard', clos: 'neutre', annule: 'neutre' };
export const TON_CONSTAT: Record<EtatConstat, TonEtat> = { synchronise: 'ok', conflit: 'retard', retenu: 'ok', ecarte: 'neutre', transmis: 'signer' };
export const TON_MESURE: Record<EtatMesure, TonEtat> = { demandee: 'attente', realisee: 'ok', non_realisee: 'retard', transmise: 'signer' };
export const TON_CONCLUSION: Record<ConclusionConstat, TonEtat> = { conforme: 'ok', non_conforme: 'retard', sans_objet: 'neutre' };

export const libelleOrdre = (e: EtatOrdreMission) => ETATS_ORDRE_MISSION[e] ?? e;
export const libelleConstat = (e: EtatConstat) => ETATS_CONSTAT[e] ?? e;
export const libelleMesure = (e: EtatMesure) => ETATS_MESURE[e] ?? e;
export const libelleConclusion = (c: ConclusionConstat | null) => (c ? CONCLUSIONS_CONSTAT[c] ?? c : 'sans constat');
export const libelleObjet = (o: ObjetOrdreMission) => OBJETS_ORDRE_MISSION[o] ?? o;

/** Marqueurs des ordres : conflit et constat non conforme en « retard », clos en « autre », sinon « ouvert ». */
export function marqueursOrdres(c: CarteControles | undefined): MarqueurCarte[] {
  return (c?.features ?? []).map((f, i) => ({
    id: f.properties.id,
    numero: i + 1,
    longitude: f.geometry.coordinates[0],
    latitude: f.geometry.coordinates[1],
    libelle: `${f.properties.numero} · ${f.properties.objet_libelle} · ${libelleOrdre(f.properties.etat)}${f.properties.conclusion ? `, ${libelleConclusion(f.properties.conclusion)}` : ''}`,
    ton: f.properties.etat === 'conflit' || f.properties.conclusion === 'non_conforme' ? ('retard' as const) : f.properties.etat === 'clos' || f.properties.etat === 'annule' ? ('autre' as const) : ('ouvert' as const),
  }));
}

export interface ReponseSaisie {
  reponse: ReponsePoint | '';
  observation: string;
}

/** Grille vide : une entrée par point du formulaire. */
export function grilleVide(points: readonly PointFormulaire[]): Record<string, ReponseSaisie> {
  return Object.fromEntries(points.map((p) => [p.code, { reponse: '', observation: '' }]));
}

/**
 * Réponses à envoyer et problèmes de la grille saisie : un point sans réponse, une observation
 * manquante ; la photo exigée est fournie après l'enregistrement, par la pièce jointe du constat, et
 * n'est donc pas bloquante ici (le point est marqué comme attendant sa photo).
 */
export function preparerReponses(points: readonly PointFormulaire[], saisie: Record<string, ReponseSaisie>): { reponses: ReponseConstat[]; problemes: string[]; photosAttendues: string[] } {
  const reponses: ReponseConstat[] = [];
  for (const p of points) {
    const s = saisie[p.code];
    if (!s || s.reponse === '') continue;
    reponses.push({ code: p.code, reponse: s.reponse, observation: s.observation.trim() || undefined });
  }
  const photosAttendues = points.filter((p) => p.photo_exigee && saisie[p.code]?.reponse && saisie[p.code]?.reponse !== 'sans_objet').map((p) => p.code);
  const problemes = verifierGrilleConstat(points, reponses.map((r) => (photosAttendues.includes(r.code) ? { ...r, photo_id_client: '00000000-0000-7000-8000-000000000000' } : r))).map((x) => x.message);
  return { reponses, problemes, photosAttendues };
}

/** Points d'un constat qui attendent encore leur photo exigée. */
export function photosManquantes(points: readonly PointFormulaire[], reponses: readonly ReponseConstat[]): PointFormulaire[] {
  return points.filter((p) => p.photo_exigee && reponses.some((r) => r.code === p.code && r.reponse !== 'sans_objet' && !r.photo_document_id));
}
